import {
	ChangeDetectorRef,
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	inject,
	ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { AiCreditsIncentiveStore } from '@jepto/features/ai-credits-incentives/stores/ai-credits-incentive.store';
import { resolveAccountShowIncentives } from '@jepto/features/ai-credits-incentives/utils/show-incentives.util';
import { ClientsInsightService } from '@jepto/features/portfolio/services/clients-insight.service';
import { JepClientsService } from '@jepto/shared/services/jep-clients/jep-clients.service';
import { JepGlobalDataService } from '@jepto/shared/services/jep-global-data/jep-global-data.service';
import { JepSignoutService } from '@jepto/shared/services/jep-signout/jep-signout.service';

import * as fromAdminStore from '@jepto/features/admin/store/index';
import * as fromPortfolioRootStore from '@jepto/features/portfolio/store/index';
import { JepBasicPlanNamePipe } from '@jepto/shared/pipes/jep-reporting-plan-name.pipe';
import * as fromSharedAccountStore from '@jepto/shared/store/account/index';
import { subscriptionPlanNameFromAccount } from '@jepto/shared/store/account/models/account.model';
import * as fromSharedStore from '@jepto/shared/store/index';
import * as fromSharedUsersStore from '@jepto/shared/store/users/index';
import { Store } from '@ngrx/store';

import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { isEmpty, filter as _filter } from 'lodash';
import { from, Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';

declare global {
	interface Window {
		Frill: any;
		FrillWidget: any;
	}
	interface Promise<T> {
		destroy?: () => void;
	}
}

import { sortKeySplitter } from '@jepto/shared/utils/collection';
import { getDiffTrialEndCurrentLocalTime } from '@jepto/shared/utils/date';
import {
	selectTopbarNotifications,
	type TopbarNotificationEligibility,
	type TopbarNotificationId,
} from './topbar-notifications.util';

import dayjs from '@jepto/config/dayjs.config';
import { error, log } from '@jepto/shared/utils/logger';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from '@jepto/environments/environment';
import {
	markForCheckOnEmit,
	safeUpdateInSubscription,
} from '@jepto/shared/utils/change-detection';

@Component({
	selector: 'jep-dash-topbar',
	templateUrl: './topbar.component.html',
	styleUrls: ['./topbar.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DashTopbarComponent implements OnInit, OnDestroy {
	private readonly aiCreditsIncentiveStore = inject(AiCreditsIncentiveStore);
	readonly isIncentivesPanelHidden = this.aiCreditsIncentiveStore.isHidden;

	jeptoAccount: any;
	plan: string = 'Agency Starter';
	profileMenus: any[];
	clientsDropdownTheme: string = 'dark';
	showTopNavShadow: boolean;
	showTopNavShadowIn = ['list', 'global-budget-tracker', 'global-kpi'];
	_isShowTopbarAccountName: boolean = false;
	showDropdownClient: boolean;
	account$: Observable<any>;
	accountDetails: any;
	unsubscribe$ = new Subject<void>();
	accountType: any;
	getUsers$: Observable<any>;
	getUserCurrentLogin: any;
	selectedClientId: string;
	showDropdownPipeline: boolean = false;
	isCollapse: boolean = false;
	durationPlan: string;
	durationDW: number;
	durationEnd: string;

	subscriptionDetails: fromAdminStore.ISubscriptionDetails;
	isDataSourcesRoute: boolean;
	unreadAnnouncementsCount: number = 0;

	constructor(
		private adminStore: Store<fromAdminStore.AdminModuleReducers>,
		private sharedStore: Store<fromSharedStore.SharedModuleReducers>,
		private portfolioStore: Store<fromPortfolioRootStore.PortfolioModuleReducers>,
		private router: Router,
		private requestService: JepRequestService,
		private jepSignoutService: JepSignoutService,
		private jepClientsService: JepClientsService,
		private jepGlobalDataService: JepGlobalDataService,
		private clientInsightService: ClientsInsightService,
		private jepPlanName: JepBasicPlanNamePipe,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		// Define widgetCallbacks once outside the subscribe block
		const widgetCallbacks = {
			onReady: (widget: any) => {
				// Store widget reference globally for other components to use
				window.FrillWidget = widget;

				const bellIcon = document.querySelector(
					'[data-test="jepto-feedback-bell"]'
				);
				if (bellIcon) {
					bellIcon.addEventListener('click', () => {
						widget.viewSection('announcements');
						widget.open();
					});
				}
			},
			onBadgeCount: (event: { count?: number }) => {
				const count =
					typeof event?.count === 'number' ? event.count : 0;
				this.unreadAnnouncementsCount = count;
				this.cdr.markForCheck();
			},
		};

		// Fetch SSO token first, then initialize container with it (per Frill SSO documentation)
		this.jepGlobalDataService
			.getFrillToken()
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe({
				next: res => {
					const frillConfig: any = {
						key: environment.frillAPI.key,
						widgets: [
							{
								key: environment.frillAPI.widgetKey,
								callbacks: widgetCallbacks,
							},
						],
					};

					// Add SSO token if available, otherwise initialize without it
					if (res.state && res.data.token) {
						frillConfig.ssoToken = res.data.token;
					}

					window.Frill('container', frillConfig);
				},
				error: err => {
					// NOTE: Log error for now and decide the handling of SSO token in future
					error(
						'Failed to fetch Frill SSO token, initializing without SSO:',
						err
					);
					// TODO: Initialize Frill without SSO token on error
				},
			});
		this.autoRun();
		this.isDataSourcesRoute = this.router.url.includes('data-sources');
		if (
			this.router.url !== '/clients' &&
			this.router.url !== '/clients/archived'
		) {
			this.loadClientsByRoute(this.router.url);
		}
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	autoRun(): void {
		this.initialiser();
		this.listeners();
	}

	initialiser(): void {
		this.loadJWTDetailsandProfileMenus();
		this.listenOnValidAuth();
		this.enableWS();
	}

	listeners(): void {
		this.getUsers$ = this.sharedStore.select(fromSharedUsersStore.getUsers);
		this.listenOnCollapseSideBar();
		this.listenOnGetSelectedClient();
		this.getCurrentUser();
		this.listenToRouterEvents();
		this.listenOnGetAccountSubscription();
		this.listenOnGetAccount();
		this.runTopbarAccountNameState(this.router.url, this.accountDetails);
	}

	listenOnCollapseSideBar(): void {
		this.jepGlobalDataService.isCollapse
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe((result: boolean) => {
				this.isCollapse = result;
			});
	}

	enableWS(): void {
		this.jepGlobalDataService.enableAllWS();
	}

	listenOnValidAuth(): void {
		const subscription = `
			subscription($id: ID!) {
				checkUserAuth(id: $id) {
					id
					isAuthorise
					accountId
				}
			}
		`;
		// Guard: token.tokens.idToken may be missing when user is not logged in (Sentry Workstream 1)
		from(fetchAuthSession()).subscribe(token => {
			const sub = token?.tokens?.idToken?.payload?.sub;
			if (sub == null) return;
			this.requestService
				.subscribe({
					query: subscription,
					variables: { id: sub },
				})
				.subscribe((result: any) => {
					log('log checkUserAuth', result);
					const response: {
						id: string;
						isAuthorise: boolean;
						accountId: string;
					} = result.data.checkUserAuth;
					// Guard: idToken may be missing when not authenticated (Sentry Workstream 1)
					from(fetchAuthSession())
						.pipe(markForCheckOnEmit(this.cdr))
						.subscribe(innerToken => {
							const accountId =
								innerToken?.tokens?.idToken?.payload?.[
									'custom:accountId'
								]?.toString();
							if (accountId == null) return;
							if (
								response.accountId !== accountId ||
								response.isAuthorise === false
							) {
								this.clientInsightService.insightsCache.activeUnderAccount =
									[];
								this.clientInsightService.insightsCache.activeUnderClient =
									[];
								this.jepSignoutService.signOutUser(
									this.unsubscribe$
								);
							}
						});
				});
		});
	}

	getCurrentUser(): void {
		this.sharedStore.dispatch(
			new fromSharedUsersStore.GetUsers({ caller: '106' })
		);
		this.getUsers$
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(getUsers => {
				// Guard: idToken may be missing when not logged in (Sentry Workstream 1)
				from(fetchAuthSession()).subscribe(token => {
					const email = token?.tokens?.idToken?.payload?.email;
					if (email == null) return;
					const user = _filter(getUsers, { email })[0];
					safeUpdateInSubscription(this.cdr, () => {
						this.getUserCurrentLogin = user;
					});
				});
			});
	}

	loadJWTDetailsandProfileMenus(): void {
		// Guard: token.tokens.idToken may be missing when not authenticated (Sentry Workstream 1)
		from(fetchAuthSession())
			.pipe(markForCheckOnEmit(this.cdr))
			.subscribe(token => {
				const payload = token?.tokens?.idToken?.payload;
				if (!payload) return;
				this.jeptoAccount = payload;
				if (!Boolean(this.jeptoAccount?.email))
					setTimeout(() => {
						this.jepSignoutService.clearJeptoCookies(
							this.unsubscribe$
						);
					});
			});
	}

	handleSelectedMenu(event: any): void {
		if (typeof event !== 'string') {
			return;
		}
		if (event === 'Logout') {
			this.clientInsightService.insightsCache.activeUnderAccount = [];
			this.clientInsightService.insightsCache.activeUnderClient = [];
			this.jepSignoutService.signOutUser(this.unsubscribe$);
		} else if (event.includes('Upgrade Plan')) {
			const accountDetails = this.accountDetails;
			const status = accountDetails?.result
				? accountDetails.result.status?.toLowerCase()
				: accountDetails?.status?.toLowerCase();
			if (status === 'in_trial') {
				this.sharedStore.dispatch(
					new fromSharedStore.Go({
						path: ['admin', 'subscription'],
					})
				);
			} else {
				this.sharedStore.dispatch(
					new fromSharedStore.Go({
						path: ['admin', 'subscription', 'update'],
					})
				);
			}
		} else if (event === 'Admin') {
			this.sharedStore.dispatch(
				new fromSharedStore.Go({ path: ['admin'] })
			);
		} else if (event === `Help Center`) {
			window.open('https://www.jepto.com/help', '_blank');
		} else if (event === `Feedback`) {
			window.FrillWidget.createIdea();
			window.FrillWidget.open();
		} else if (event === `Changelog`) {
			window.open('https://www.jepto.com/changelog', '_blank');
		} else {
			this.jepSignoutService.clearJeptoCookies(this.unsubscribe$);
		}
	}

	listenOnGetSelectedClient(): void {
		this.portfolioStore
			.select(fromPortfolioRootStore.selectedClient)
			.pipe(
				debounceTime(100),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((result: any) => {
				if (
					Boolean(result) &&
					!isEmpty(result) &&
					Boolean(result.selected) &&
					Boolean(result.sortKey)
				) {
					this.selectedClientId =
						result.id || sortKeySplitter(result.sortKey, '#', 2);
				} else {
					if (
						(result !== undefined &&
							result.value === 'all_clients') ||
						(result !== undefined && result.selected === false)
					) {
						this.selectedClientId = '';
					}
				}
			});
	}

	listenToRouterEvents(): void {
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((event: NavigationEnd) => {
				this.isDataSourcesRoute = event.url.includes('data-sources');
				this.runTopbarAccountNameState(event.url, this.accountDetails);
				if (
					event.url !== '/clients' &&
					event.url !== '/clients/archived' &&
					!event.url.includes('get-started')
				) {
					this.loadClientsByRoute(event.url);
				}
				this.showDropdownClient =
					event.url === '/clients' ||
					event.url === '/clients/archived'
						? false
						: true;

				this.showDropdownPipeline = event.url.includes(
					'/pipelines/portfolio'
				)
					? true
					: false;
			});
	}

	loadClientsByRoute(url: string): void {
		const origin = this.getClientRequestOrigin(url);
		log(`${this.constructor.name} requestOrigin`, origin, url);
		this.jepClientsService.runGetActiveClients('top_menu', null, origin);
	}

	getClientRequestOrigin(
		url: string
	):
		| 'portfolio'
		| 'global_kpi'
		| 'selected_client_kpi'
		| 'global_budget'
		| 'selected_client_budget'
		| 'global_insight'
		| 'selected_client_insight'
		| 'global_anomaly'
		| 'selected_client_anomaly'
		| 'global_automation'
		| 'selected_client_automation'
		| 'data_sources'
		| 'datachat' {
		// Check if this is a client-specific route
		const isClientRoute =
			url.includes('/clients/') && !url.includes('/clients/archived');

		if (isClientRoute) {
			const match = url.match(/\/clients\/[^/]+\/(.*)/);
			if (match) {
				const extractedString = match[1]; // This will contain "kpis", "anomalies", etc.
				const routeType = extractedString.split('/')[0];

				// Map client-specific routes to selected_client_* variants
				switch (routeType) {
					case 'chats':
						return 'datachat';
					case 'kpis':
						return 'selected_client_kpi';
					case 'budgets':
						return 'selected_client_budget';
					case 'insights':
						return 'selected_client_insight';
					case 'anomalies':
						return 'selected_client_anomaly';
					case 'automations':
					case 'posts':
					case 'local':
					case 'reviews':
					case 'ai-visibility':
						return 'selected_client_automation';
					default:
						return 'selected_client_kpi'; // Default fallback for client routes
				}
			}
		}

		// Handle global routes (no /clients/{id}/ prefix)
		if (url === '/ai-visibility' || url.startsWith('/ai-visibility/')) {
			return 'global_automation';
		}
		switch (url) {
			case '/chats':
				return 'datachat';
			case '/kpis':
			case '/kpis/archived':
				return 'global_kpi';
			case '/clients/archived':
				return 'portfolio';
			case '/budgets':
			case '/budgets/archived':
				return 'global_budget';
			case '/insights':
			case '/insights/resolved':
				return 'global_insight';
			case '/anomalies':
				return 'global_anomaly';
			case '/automations':
			case '/posts':
			case '/local':
			case '/reviews/profiles':
				return 'global_automation';
			case '/data-sources':
				return 'data_sources';
			default:
				return 'global_kpi';
		}
	}

	listenOnGetAccount(): void {
		this.sharedStore
			.select(fromSharedAccountStore.getAccountData)
			.pipe(
				tap(res => log('clg listenOnGetAccount', res)),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((result: any) => {
				if (!result) {
					return;
				}
				this.accountDetails = result;
				const planName = subscriptionPlanNameFromAccount(result);
				if (planName) {
					this.accountType = planName;
				}
				if (this.accountDetails.status === 'in_trial') {
					this.getPlanTrialEnds();
				}
				if (this.accountDetails.status === 'non_renewing') {
					this.getNonRenewingAccount();
				}
				this.runTopbarAccountNameState(this.router.url, result);
			});
	}

	getNonRenewingAccount(): void {
		const termEnd = this.accountDetails?.subscription?.currentTermEnd;
		if (!termEnd) {
			return;
		}
		const now = dayjs();
		const end = dayjs(termEnd);
		const diff = end.diff(now, 'days');
		if (diff > 0) {
			this.durationPlan = `in ${diff} ${Number(diff) === 1 ? 'day' : 'days'}`;
		} else {
			this.durationPlan = `today`;
		}
		log('getNonRenewingAccount ', this.durationPlan);
	}

	listenOnGetAccountSubscription() {
		this.adminStore
			.select(fromAdminStore.getSubscriptionAccountDetails)
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe((result: fromAdminStore.ISubscriptionDetails) => {
				if (Boolean(result)) {
					safeUpdateInSubscription(this.cdr, () => {
						this.subscriptionDetails = result;
						log(
							'clg subscriptionDetails',
							this.subscriptionDetails
						);
						if (
							this.subscriptionDetails.dataWarehouseTrial
								.isTrialStarted &&
							this.subscriptionDetails.status === 'active'
						) {
							this.durationDW = getDiffTrialEndCurrentLocalTime(
								this.subscriptionDetails.dataWarehouseTrial
									.endDate,
								this.subscriptionDetails.account.timezone,
								'days'
							);
						}
					});
				}
			});
	}

	getPlanTrialEnds(): void {
		if (!this.accountDetails?.hasCard) {
			const currentDate = dayjs();
			const newDate = currentDate.add(13, 'days');
			let trialEnd = newDate.format('YYYY-MM-DDTHH:mm:ss');
			const subscriptionTrialEnd =
				this.accountDetails?.subscription?.trialEnd;
			if (subscriptionTrialEnd) {
				trialEnd = subscriptionTrialEnd;
			}
			const diff = getDiffTrialEndCurrentLocalTime(
				trialEnd,
				this.accountDetails.timezone,
				'days'
			);
			if (diff > 0) {
				this.durationPlan = `in ${diff} ${Number(diff) === 1 ? 'day' : 'days'}`;
			} else {
				this.durationPlan = `today`;
			}
		}
	}

	mergePortfolioLabelToAccountDetails(
		accountDetails: any,
		page: string
	): any {
		return { ...accountDetails, page: page };
	}

	mergeAdminLabelToAccountDetails(accountDetails: any): any {
		return { ...accountDetails, page: 'Admin' };
	}

	runTopbarAccountNameState(currentUrl: string, accountDetails: any): void {
		if (
			currentUrl.includes('/kpis') ||
			currentUrl.includes('/budgets') ||
			currentUrl.includes('/anomalies') ||
			currentUrl.includes('/automation') ||
			currentUrl.includes('/insights') ||
			currentUrl.includes('/local') ||
			currentUrl.includes('/reviews') ||
			currentUrl.includes('/ai-visibility') ||
			currentUrl.includes('/posts') ||
			currentUrl.includes('/data-sources') ||
			currentUrl.includes('/chats') ||
			currentUrl.includes('/reports')
		) {
			this.isShowTopbarAccountName = false;
			this.showDropdownPipeline = false;
			this.showDropdownClient = true;
		} else if (
			currentUrl.includes('/clients') ||
			currentUrl.includes('/pipelines') ||
			currentUrl === '/extension'
		) {
			if (currentUrl.includes('settings')) {
				this.showDropdownPipeline = false;
				this.isShowTopbarAccountName = false;
				this.showDropdownClient = true;
			} else {
				this.showDropdownPipeline = false;
				this.isShowTopbarAccountName = true;
				this.showDropdownClient = false;
				this.accountDetails = this.mergePortfolioLabelToAccountDetails(
					accountDetails,
					currentUrl.includes('/pipelines')
						? 'Data Warehouse'
						: 'Portfolio'
				);
			}
		} else if (currentUrl.includes('/admin')) {
			this.isShowTopbarAccountName = true;
			this.showDropdownPipeline = false;
			this.accountDetails =
				this.mergeAdminLabelToAccountDetails(accountDetails);
		} else if (
			currentUrl.includes('/analysis') ||
			currentUrl.includes('/extensions')
		) {
			this.showDropdownPipeline = true;
			this.isShowTopbarAccountName = false;
			this.showDropdownClient = false;
			this.accountType = null;
		} else if (currentUrl.includes('/get-started')) {
			this.showDropdownPipeline = false;
			this.isShowTopbarAccountName = true;
			this.showDropdownClient = false;
		} else if (currentUrl.includes('/settings')) {
			this.showDropdownPipeline = false;
			this.isShowTopbarAccountName = false;
			this.showDropdownClient = true;
		} else {
			this.isShowTopbarAccountName = false;
			this.showDropdownClient = false;
			this.showDropdownPipeline = false;
		}

		if (
			accountDetails &&
			accountDetails.subscription &&
			accountDetails.subscription.planName
		)
			this.plan = accountDetails.subscription.planName;
		this.profileMenus = [
			`Admin`,
			`<span style="font-weight: normal; font-size:0.8rem; ">Upgrade Plan</span>
             <span style="font-size: 0.6875rem; font-weight: normal; color: #FFFFFF; line-height: 23px; border-radius: 12px; background-color: #651fff; display: inline-block; padding: 0 8px; margin-left: 5px;">
             ${this.jepPlanName.transform(this.plan)}
             </span>`,
			`Clear Jepto Cookies`,
		];
	}

	// `Help Center <i class="icon-external-link"> </i>`,
	// `Feedback <i class="icon-external-link"> </i>`,
	// `Changelog <i class="icon-external-link"> </i>`,
	// `Logout`

	set isShowTopbarAccountName(value: boolean) {
		this._isShowTopbarAccountName = value;
	}

	get isShowTopbarAccountName() {
		return this._isShowTopbarAccountName;
	}

	get topbarNotificationEligibility(): TopbarNotificationEligibility {
		const status = this.accountDetails?.status;
		const cardStatus = this.accountDetails?.subscription?.cardStatus;
		const dwTrial =
			status === 'active' &&
			(this.subscriptionDetails?.dataWarehouseTrial?.isTrialStarted ??
				false) &&
			this.subscriptionDetails?.dataWarehouseTrial?.isTrialEnded ===
				false &&
			this.durationDW > 0;

		return {
			paymentFailed: this.accountDetails?.transactionStatus === 'failure',
			cardExpired: cardStatus === 'expired',
			cardExpiring: cardStatus === 'expiring',
			jeptoTrial: status === 'in_trial',
			subscriptionEnding: status === 'non_renewing',
			dwTrial,
			incentive:
				resolveAccountShowIncentives(this.accountDetails) &&
				!this.isIncentivesPanelHidden(),
		};
	}

	get visibleTopbarNotificationIds(): TopbarNotificationId[] {
		return selectTopbarNotifications(this.topbarNotificationEligibility);
	}

	isTopbarNotificationVisible(id: TopbarNotificationId): boolean {
		return this.visibleTopbarNotificationIds.includes(id);
	}

	get hasBillingBanner(): boolean {
		return this.visibleTopbarNotificationIds.some(
			id => id !== 'incentive'
		);
	}

	changePlan(): void {
		// this.intercom.showNewMessage('I would like to inquire about changing my plan.');
	}

	subscribeNow(): void {
		this.router.navigate(['admin', 'subscription']);
	}

	addCreditsNow(): void {
		this.router.navigate(['admin', 'subscription']);
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		if (event.target.innerWidth <= 1024) {
			this.clientsDropdownTheme = 'light';
		} else {
			this.clientsDropdownTheme = 'dark';
		}
	}
}
